import { memo, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { cn } from "../../ui/utils";
import type { ChartSpec } from "../types";
import { CHART_HEIGHT, CHART_THEME_CLASS } from "./chartTheme";
import { CartesianChart } from "./CartesianChart";
import { RadialChart } from "./RadialChart";
import { ChartEmpty, ChartError, ChartNoData, ChartSkeleton } from "./ChartStates";
import {
  buildSrSummary,
  deriveState,
  isAllZero,
  isRadial,
  normalizeChart,
} from "./resolveChart";

interface ChartMessageProps {
  spec?: ChartSpec;
  loading?: boolean;
  className?: string;
}

function ChartCard({
  title,
  subtitle,
  className,
  children,
}: {
  title?: string;
  subtitle?: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <figure
      className={cn(
        // Shared neutral-block surface + 12px (px-3) inset, identical to
        // AssistantTable, so charts and tables align and read as one family.
        "my-1.5 rounded-xl border border-border/50 bg-muted/30 px-3 py-3",
        "dark:border-white/[0.06] dark:bg-white/[0.02]",
        className,
      )}
    >
      {(title || subtitle) && (
        <figcaption className="mb-3">
          {title && <p className="text-xs font-semibold leading-snug text-foreground">{title}</p>}
          {subtitle && (
            <p className="mt-0.5 text-[0.6875rem] leading-snug text-muted-foreground">{subtitle}</p>
          )}
        </figcaption>
      )}
      {children}
    </figure>
  );
}

function useInView<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (inView) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          io.disconnect();
        }
      },
      { rootMargin: "120px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [inView]);

  return { ref, inView };
}

export const ChartMessage = memo(function ChartMessage({ spec, loading, className }: ChartMessageProps) {
  const state = deriveState(spec, loading);
  const normalized = useMemo(
    () => (state === "success" && spec ? normalizeChart(spec) : null),
    [state, spec],
  );
  const { ref, inView } = useInView<HTMLDivElement>();

  const effective = state === "success" && normalized && isAllZero(normalized) ? "empty" : state;

  if (effective === "loading") {
    return (
      <ChartCard title={spec?.title} subtitle={spec?.subtitle} className={className}>
        <ChartSkeleton />
      </ChartCard>
    );
  }
  if (effective === "error") {
    return (
      <ChartCard title={spec?.title} className={className}>
        <ChartError message={spec?.message} />
      </ChartCard>
    );
  }
  if (effective === "nodata") {
    return (
      <ChartCard title={spec?.title} className={className}>
        <ChartNoData message={spec?.message} />
      </ChartCard>
    );
  }
  if (effective === "empty") {
    return (
      <ChartCard title={spec?.title} className={className}>
        <ChartEmpty message={spec?.message} />
      </ChartCard>
    );
  }
  if (!normalized) return null;

  const radial = isRadial(normalized.kind);
  const summary = buildSrSummary(normalized);

  return (
    <ChartCard title={normalized.title} subtitle={normalized.subtitle} className={className}>
      {/* @container lets the renderers adapt to the chart's own width (not the
          viewport), so the layout is correct inside the fixed-width chat panel. */}
      <div ref={ref} role="img" aria-label={summary} className={cn("@container relative", CHART_THEME_CLASS)}>
        {inView ? (
          radial ? (
            <RadialChart chart={normalized} />
          ) : (
            <CartesianChart chart={normalized} />
          )
        ) : (
          <ChartSkeleton height={radial ? CHART_HEIGHT.radial : CHART_HEIGHT.cartesian} />
        )}
      </div>
    </ChartCard>
  );
});
